<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import loginSide1 from '../assets/login-side-1.webp'
import loginSide2 from '../assets/login-side-2.webp'
import loginSide3 from '../assets/login-side-3.webp'
import loginSide4 from '../assets/login-side-4.webp'

import { useI18n } from '@/shared/i18n'

const { t } = useI18n()

const slides = computed(() => [
  {
    image: loginSide1,
    alt: t('auth.brand.analystAlt'),
    title: t('auth.brand.analystTitle'),
    accent: t('auth.brand.analystAccent'),
    description: t('auth.brand.analystDescription'),
  },
  {
    image: loginSide2,
    alt: t('auth.brand.creativesAlt'),
    title: t('auth.brand.creativesTitle'),
    accent: t('auth.brand.creativesAccent'),
    description: t('auth.brand.creativesDescription'),
    // Legacy `AuthSlider.vue`: the creatives artwork spans the panel; the rest sit at 3/4 width.
    fullWidth: true,
  },
  {
    image: loginSide3,
    alt: t('auth.brand.impactAlt'),
    title: t('auth.brand.impactTitle'),
    accent: t('auth.brand.impactAccent'),
    description: t('auth.brand.impactDescription'),
  },
  {
    image: loginSide4,
    alt: t('auth.brand.channelsAlt'),
    title: t('auth.brand.channelsTitle'),
    accent: t('auth.brand.channelsAccent'),
    description: t('auth.brand.channelsDescription'),
  },
])

const activeIndex = ref(0)
// All slides stay mounted for the crossfade, but only the active image may compete with the
// login form's first paint — the rest mount shortly after, well before the first rotation.
const deferredSlidesReady = ref(false)
let interval: ReturnType<typeof setInterval> | undefined
let deferredTimer: ReturnType<typeof setTimeout> | undefined

function selectSlide(index: number): void {
  activeIndex.value = index
}

function stopRotation(): void {
  if (interval !== undefined) clearInterval(interval)
  interval = undefined
}

function startRotation(): void {
  stopRotation()
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  interval = setInterval(() => {
    activeIndex.value = (activeIndex.value + 1) % slides.value.length
  }, 2500)
}

onMounted(() => {
  startRotation()
  deferredTimer = setTimeout(() => {
    deferredSlidesReady.value = true
  }, 300)
})
onBeforeUnmount(() => {
  stopRotation()
  if (deferredTimer !== undefined) clearTimeout(deferredTimer)
})
</script>

<template>
  <aside
    class="auth-brand-panel relative min-w-0 min-h-dvh overflow-hidden text-primary-fg"
    :aria-label="t('auth.brand.label')"
    @mouseenter="stopRotation"
    @mouseleave="startRotation"
    @focusin="stopRotation"
    @focusout="startRotation"
  >
    <figure
      v-for="(slide, index) in slides"
      :key="slide.title"
      class="auth-brand-panel__slide absolute inset-0 m-0 opacity-0 transition-[opacity] duration-1000 ease-[ease-in-out] pointer-events-none motion-reduce:transition-none"
      :class="{ 'auth-brand-panel__slide--active': activeIndex === index }"
      :aria-hidden="activeIndex !== index"
    >
      <img
        v-if="index === activeIndex || deferredSlidesReady"
        :src="slide.image"
        :alt="slide.alt"
        :class="'fullWidth' in slide && slide.fullWidth ? 'w-full' : 'mx-auto w-9/12'"
      />
      <figcaption>
        <p class="auth-brand-panel__headline m-0 text-xl font-semibold leading-7">
          <span>{{ slide.title }}</span>
          <strong>{{ slide.accent }}</strong>
        </p>
        <p
          class="auth-brand-panel__description max-w-160 mt-0 mx-auto mb-0 text-[color-mix(in_srgb,_var(--ui-color-primary-fg)_60%,_transparent)] text-sm leading-5"
        >
          {{ slide.description }}
        </p>
      </figcaption>
    </figure>

    <div
      class="auth-brand-panel__controls absolute bottom-md left-1/2 z-1 flex gap-xs transform-[translateX(-50%)]"
      role="group"
      :aria-label="t('auth.brand.controls')"
    >
      <button
        v-for="(_, index) in slides"
        :key="index"
        type="button"
        :class="{ 'auth-brand-panel__dot--active': activeIndex === index }"
        :aria-label="t('auth.brand.goToSlide', { position: index + 1 })"
        :aria-pressed="activeIndex === index"
        @click="selectSlide(index)"
      />
    </div>
  </aside>
</template>

<style scoped>
.auth-brand-panel {
  background: var(--ui-gradient-auth-panel);
}
.auth-brand-panel .auth-brand-panel__slide--active {
  opacity: 100%;
  pointer-events: auto;
}
.auth-brand-panel .auth-brand-panel__slide img {
  height: 100%;
  padding-bottom: 6rem;
  object-fit: contain;
}
.auth-brand-panel .auth-brand-panel__slide figcaption {
  position: absolute;
  right: 0;
  bottom: 4.5rem;
  left: 0;
  padding-inline: 1rem;
  opacity: 0%;
  transform: translateY(1rem);
  text-align: center;
  transition-property: opacity, transform;
  transition-timing-function: ease 0.3s;
  transition-duration: 500ms;
}
.auth-brand-panel .auth-brand-panel__slide--active figcaption {
  opacity: 100%;
  transform: translateY(0);
}
.auth-brand-panel .auth-brand-panel__headline strong {
  color: var(--ui-color-accent);
}
.auth-brand-panel .auth-brand-panel__controls button {
  width: 0.5rem;
  height: 0.5rem;
  padding: 0;
  border-style: solid;
  border-width: 0px;
  border-radius: var(--ui-radius-full);
  background-color: color-mix(in srgb, var(--ui-color-primary-fg) 20%, transparent);
  transition-property: background;
  transition-timing-function: ease;
  transition-duration: 200ms;
}
.auth-brand-panel .auth-brand-panel__controls button:focus-visible {
  outline-style: solid;
  outline-width: 2px;
  outline-color: var(--ui-color-primary-fg);
  outline-offset: 3px;
}
.auth-brand-panel .auth-brand-panel__controls .auth-brand-panel__dot--active {
  background-color: var(--ui-color-primary-fg);
}
</style>
